import { useCallback, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import analyticsService from '../../services/analytics/analytics'

/**
 * Custom hook for analytics tracking
 * Provides easy-to-use methods for tracking events, page views, and user properties
 */
export const useAnalytics = () => {
  const location = useLocation()

  // Track page views on route change
  useEffect(() => {
    const title = document.title || 'Et Ai'
    const path = location.pathname + location.search

    analyticsService.trackPageView(path, title)
  }, [location])

  // Track custom events
  const trackEvent = useCallback((eventName, parameters = {}) => {
    analyticsService.trackEvent(eventName, parameters)
  }, [])

  // Track user engagement
  const trackEngagement = useCallback((category, action, label, value) => {
    analyticsService.trackEngagement(category, action, label, value)
  }, [])

  // Track errors
  const trackError = useCallback((description, fatal = false) => {
    analyticsService.trackError(description, fatal)
  }, [])

  // Track search/chat
  const trackSearch = useCallback((query, type = 'search') => {
    analyticsService.trackSearch(query, type)
  }, [])

  // Track timing
  const trackTiming = useCallback((category, variable, value, label) => {
    analyticsService.trackTiming(category, variable, value, label)
  }, [])

  // Track conversions
  const trackConversion = useCallback((conversionType, data = {}) => {
    analyticsService.trackConversion(conversionType, data)
  }, [])

  // Set user properties
  const setUserProperties = useCallback((properties) => {
    analyticsService.setUserProperties(properties)
  }, [])

  // Track authentication
  const trackAuth = useCallback((method, userId, isNewUser = false) => {
    analyticsService.trackAuth(method, userId, isNewUser)
  }, [])

  // Track sign out
  const trackSignOut = useCallback(() => {
    analyticsService.trackSignOut()
  }, [])

  return {
    trackEvent,
    trackEngagement,
    trackError,
    trackSearch,
    trackTiming,
    trackConversion,
    setUserProperties,
    trackAuth,
    trackSignOut,
  }
}

/**
 * Hook for tracking performance metrics
 * Useful for measuring load times, API response times, etc.
 */
export const usePerformanceTracking = () => {
  const { trackTiming } = useAnalytics()

  const measureTime = useCallback(
    (category, variable, label) => {
      const startTime = performance.now()

      return () => {
        const endTime = performance.now()
        const duration = endTime - startTime
        trackTiming(category, variable, duration, label)
        return duration
      }
    },
    [trackTiming]
  )

  const trackApiCall = useCallback(
    async (apiCall, endpoint) => {
      const startTime = performance.now()

      try {
        const result = await apiCall()
        const duration = performance.now() - startTime

        trackTiming('api', endpoint, duration, 'success')

        return result
      } catch (error) {
        const duration = performance.now() - startTime

        trackTiming('api', endpoint, duration, 'error')

        throw error
      }
    },
    [trackTiming]
  )

  return {
    measureTime,
    trackApiCall,
  }
}

/**
 * Hook for tracking user interactions with specific components
 */
export const useComponentTracking = (componentName) => {
  const { trackEngagement } = useAnalytics()

  const trackClick = useCallback(
    (action, label) => {
      trackEngagement(componentName, `click_${action}`, label)
    },
    [componentName, trackEngagement]
  )

  const trackView = useCallback(
    (label) => {
      trackEngagement(componentName, 'view', label)
    },
    [componentName, trackEngagement]
  )

  const trackInteraction = useCallback(
    (action, label, value) => {
      trackEngagement(componentName, action, label, value)
    },
    [componentName, trackEngagement]
  )

  return {
    trackClick,
    trackView,
    trackInteraction,
  }
}
